import { useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiClient as api } from '@/lib/api';
import type { ApiKeyWire } from '@easyflow/shared';
import { Alert, AlertDescription } from '@easyflow/ui-frontend/components/ui/alert';

const PK_PLACEHOLDER = 'INSERISCI_LA_TUA_PUBLIC_KEY';

interface PublicKeySummary {
  id: string;
  name: string;
  prefix: string;
  /** Browser-safe pk_ value, returned by `/api-keys` for `key_type=public`. */
  public_value?: string | null;
  allowed_origins?: string[];
  last_used_at?: string | null;
}

export interface EmbedCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /**
   * Snippet builder. Accepts the prefix of the bound public key (when one
   * exists) so we can include it as a non-secret hint in the snippet — the
   * full pk_ value is set by the user via "Chiave pubblica" dialog and is
   * never re-emitted by the embed dialog.
   */
  embedCode: string | ((publicKeyPrefix?: string) => string);
  previewUrl?: string;
  formId?: string;
  /** Callback to open the dedicated "Chiave pubblica" dialog. */
  onOpenPublicKeyDialog?: () => void;
}

/**
 * Task #507 — simplified Embed dialog: only renders the snippet + Copy +
 * Anteprima. Public-key generation lives in the dedicated PublicKeyDialog.
 *
 * When the bound form has zero public keys we render a clear call-to-action
 * to open the key dialog first; the snippet is shown read-only with a
 * placeholder so users understand it cannot work without a pk_.
 */
export default function EmbedCodeDialog({
  open,
  onOpenChange,
  title,
  description,
  embedCode,
  previewUrl,
  formId,
  onOpenPublicKeyDialog,
}: EmbedCodeDialogProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [pkInput, setPkInput] = useState('');

  const { data: keys } = useQuery<PublicKeySummary[]>({
    queryKey: ['public-api-keys', formId],
    enabled: open && !!formId,
    queryFn: async () => {
      const res = await api.get<ApiKeyWire[]>(`/api-keys?formId=${formId}`);
      // Defensive double-unwrap: `res.data` is already the array, but tolerate
      // a legacy double-envelope (`{ data: { data: [...] } }`) at runtime.
      const d: unknown = res.data;
      const list = Array.isArray(d) ? d : ((d as { data?: ApiKeyWire[] } | null)?.data ?? []);
      return list as PublicKeySummary[];
    },
  });

  const hasKey = (keys?.length ?? 0) > 0;
  const firstKey = keys?.[0];
  const firstPrefix = firstKey?.prefix;
  const storedPk = firstKey?.public_value || '';

  // Task #507 — pk_ values are browser-safe and persisted plaintext server
  // side, so the dialog can inject them automatically. Manual paste is kept
  // only as a fallback for legacy keys created before the public_value
  // column existed.
  const trimmedPk = pkInput.trim();
  const validPk = /^pk_(live|test)_[A-Za-z0-9]{8,}$/.test(trimmedPk);
  const matchesPrefix = !firstPrefix || trimmedPk.startsWith(firstPrefix);
  const effectivePk = storedPk || (validPk && matchesPrefix ? trimmedPk : '');

  const baseSnippet = useMemo(() => {
    if (typeof embedCode === 'string') return embedCode;
    return embedCode(firstPrefix);
  }, [embedCode, firstPrefix]);

  const computedEmbed = useMemo(() => {
    if (effectivePk) return baseSnippet.split(PK_PLACEHOLDER).join(effectivePk);
    return baseSnippet;
  }, [baseSnippet, effectivePk]);

  const stillHasPlaceholder = computedEmbed.includes(PK_PLACEHOLDER);
  const canCopy = hasKey && !stillHasPlaceholder;

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = () => {
    if (!hasKey) {
      toast({
        title: 'Chiave pubblica mancante',
        description: 'Genera prima una pk_ dal pulsante "Chiave pubblica".',
        variant: 'destructive',
      });
      return;
    }
    if (!canCopy) {
      toast({
        title: 'Incolla la pk_ completa',
        description: firstPrefix
          ? `Incolla la chiave intera che inizia con ${firstPrefix} per abilitare la copia.`
          : 'Incolla la chiave pubblica completa (pk_live_…) per abilitare la copia.',
        variant: 'destructive',
      });
      return;
    }
    navigator.clipboard.writeText(computedEmbed);
    setCopied(true);
    toast({ title: 'Copiato!', description: 'Codice embed copiato negli appunti' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title || 'Codice Embed'}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4">
          {!hasKey && (
            <Alert>
              <AlertDescription className="text-sm">
                Per pubblicare questo widget devi prima generare una <strong>chiave pubblica</strong> (<code>pk_live_…</code>).
                Senza la chiave il widget verrà rifiutato dal backend con un errore di autenticazione.
                {onOpenPublicKeyDialog && (
                  <div className="mt-2">
                    <Button size="sm" variant="default" onClick={onOpenPublicKeyDialog}>
                      Apri "Chiave pubblica"
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {hasKey && firstKey && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs space-y-1">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-muted-foreground">Chiave usata:</span>
                <code className="font-mono text-[12px]">{firstPrefix}…</code>
                <span className="text-muted-foreground">·</span>
                <span>{firstKey.name}</span>
                {firstKey.last_used_at && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      ultimo uso {new Date(firstKey.last_used_at).toLocaleString()}
                    </span>
                  </>
                )}
              </div>
              {firstKey.allowed_origins && firstKey.allowed_origins.length > 0 && (
                <div className="text-muted-foreground">
                  Origins: {firstKey.allowed_origins.map((o) => (
                    <code key={o} className="font-mono mr-1">{o}</code>
                  ))}
                </div>
              )}
            </div>
          )}

          {hasKey && !storedPk && (
            <div className="space-y-1">
              <Label className="text-xs">
                Chiave legacy senza valore memorizzato — incolla la pk_ completa{firstPrefix ? ` (inizia con ${firstPrefix})` : ''}
              </Label>
              <Input
                type="text"
                value={pkInput}
                onChange={(e) => setPkInput(e.target.value)}
                placeholder={firstPrefix ? `${firstPrefix}…` : 'pk_live_…'}
                className="font-mono text-sm min-h-[44px]"
                autoComplete="off"
                spellCheck={false}
              />
              {pkInput && !validPk && (
                <p className="text-xs text-red-500">Formato chiave non valido (atteso pk_live_… o pk_test_…).</p>
              )}
              {pkInput && validPk && !matchesPrefix && (
                <p className="text-xs text-red-500">La chiave non corrisponde al prefisso registrato per questo form.</p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={handleCopy}
              disabled={!canCopy}
              title={!hasKey
                ? 'Genera prima una chiave pubblica'
                : !canCopy
                  ? 'Incolla la chiave pubblica completa per abilitare la copia'
                  : ''}
              className={`min-h-[44px] ${copied ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {copied ? '✓ Copiato!' : 'Copia codice'}
            </Button>
            {previewUrl && (
              <Button variant="outline" asChild className="min-h-[44px]">
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apri anteprima
                </a>
              </Button>
            )}
            <p className="text-sm text-gray-500">
              Incolla questo snippet nel tag &lt;body&gt; della tua pagina HTML.
            </p>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-[50vh] overflow-y-auto">
            <pre className="text-green-400 text-sm whitespace-pre-wrap font-mono">{computedEmbed}</pre>
          </div>

          <p className="text-xs text-muted-foreground">
            La chiave pubblica viene gestita separatamente dal pulsante <strong>Chiave pubblica</strong> sulla riga del form.
            Per copiare uno snippet pronto incolla qui sopra la <code>pk_</code> completa: la sostituiremo automaticamente al
            posto del placeholder <code>INSERISCI_LA_TUA_PUBLIC_KEY</code> e abiliteremo il pulsante di copia.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
